// src/App.jsx
import { useEffect, useMemo, useState } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LoginPage, RegisterPage, CompleteSetupPage, JoinShopPage } from './components/auth/AuthPages';
import { inviteCodeFromUrl } from './lib/invites';
import { AppLayout } from './components/layout/AppLayout';
import { WorkboardPage } from './pages/WorkboardPage';
import { DashboardPage } from './pages/DashboardPage';
import { NewOrderPage } from './pages/NewOrderPage';
import { OrdersPage } from './pages/OrdersPage';
import { CustomersPage } from './pages/CustomersPage';
import { ProductsPage, EmployeesPage, ReportsPage, SettingsPage } from './pages/OtherPages';
import { subscribeRecentOrders } from './lib/db';
import { buildWorkboard } from './lib/priority';
import './index.css';

/**
 * Count of orders that need hands on them right now. Drives the sidebar badge
 * so the number is visible from any tab — you don't have to be looking at the
 * workboard to know something's waiting.
 */
function useUrgentCount(shopId, shop) {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!shopId) return;
    return subscribeRecentOrders(shopId, { onData: setOrders });
  }, [shopId]);

  return useMemo(
    () => buildWorkboard(orders, shop)
      .filter(o => ['late', 'today', 'flight', 'start'].includes(o.sched.urgency))
      .length,
    [orders, shop]
  );
}

function AppInner() {
  const { user, profile, loading, shopId, shop, profileState } = useAuth();
  // Arriving on an invite link drops you straight on the join screen.
  const [authMode, setAuthMode] = useState(
    () => (inviteCodeFromUrl() ? 'join' : 'login')   // 'login' | 'register' | 'join'
  );
  const [page,     setPage]     = useState('workboard');
  const urgentCount = useUrgentCount(shopId, shop);

  if (loading) {
    return (
      <div style={{
        minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
        background:'var(--g800)',flexDirection:'column',gap:16
      }}>
        <div style={{fontSize:40}}>💻</div>
        <div className="spinner" style={{borderTopColor:'var(--g400)',borderColor:'rgba(255,255,255,0.15)'}}/>
      </div>
    );
  }

  if (!user) {
    if (authMode === 'join')     return <JoinShopPage onSwitch={() => setAuthMode('login')}/>;
    if (authMode === 'register') return <RegisterPage onSwitch={() => setAuthMode('login')}/>;
    return (
      <LoginPage
        onSwitch={() => setAuthMode('register')}
        onJoin={() => setAuthMode('join')}
      />
    );
  }

  // Signed in. Only offer the setup form when we are certain there is no shop —
  // a slow or failed profile read must not be mistaken for a missing shop, or
  // the owner gets asked to create their shop a second time.
  if (profileState === 'loading') {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',
                   background:'var(--g800)',flexDirection:'column',gap:16}}>
        <div style={{fontSize:40}}>💐</div>
        <div className="spinner" style={{borderTopColor:'var(--g400)',borderColor:'rgba(255,255,255,0.15)'}}/>
      </div>
    );
  }
  if (profileState === 'error') return <ProfileErrorPage/>;
  if (profileState === 'missing' || !profile) return <CompleteSetupPage/>;

  const pages = {
    workboard: <WorkboardPage setPage={setPage}/>,
    dashboard: <DashboardPage setPage={setPage}/>,
    orders:    <OrdersPage/>,
    'new-order': <NewOrderPage setPage={setPage}/>,
    customers: <CustomersPage/>,
    products:  <ProductsPage/>,
    employees: <EmployeesPage/>,
    reports:   <ReportsPage/>,
    settings:  <SettingsPage/>,
  };

  return (
    <AppLayout page={page} setPage={setPage} actionCount={urgentCount}>
      {pages[page] || <WorkboardPage setPage={setPage}/>}
    </AppLayout>
  );
}

/**
 * Shown when the profile could not be read at all (offline, permissions).
 * Deliberately NOT the setup form — a failed read is not a missing shop, and
 * offering setup here would create a duplicate.
 */
function ProfileErrorPage() {
  const { retryProfile, logout, user } = useAuth();
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-mark">⚠️</div>
          <div className="auth-logo-text">AGT<span>POS</span></div>
          <div className="auth-subtitle">Couldn't load your shop</div>
        </div>
        <p style={{fontSize:13,color:'var(--text-mid)',marginBottom:16,textAlign:'center'}}>
          You're signed in as {user?.email}, but we couldn't reach your shop's
          details. This is usually the internet connection.
        </p>
        <button className="btn btn-primary" style={{width:'100%',marginBottom:10}} onClick={retryProfile}>
          Try again
        </button>
        <button className="btn btn-ghost" style={{width:'100%'}} onClick={logout}>Sign out</button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner/>
    </AuthProvider>
  );
}
